import { Button, ColorPicker, Flex, Input, InputNumber, Tooltip, Typography } from 'antd';
import { LuCheck, LuX } from 'react-icons/lu';

const { Text } = Typography;

/**
 * Cost Summary class row in edit mode: color, label and price on one
 * non-wrapping line, then save/cancel. Edits live in the caller's `draft` and
 * only reach the filter on save.
 *
 * @param {object} props
 * @param {{color, label, pricePerM2}} props.draft - uncommitted edit
 * @param {(updater: (d: object) => object) => void} props.setDraft
 * @param {{symbol}} props.currency - resolved currency for the price suffix
 * @param {() => void} props.onSave
 * @param {() => void} props.onCancel
 * @param {object} [props.style] - row container style (padding/divider)
 */
const ClassEditRow = ({ draft, setDraft, currency, onSave, onCancel, style }) => {
	// Escape cancels; stopPropagation keeps it from also closing the drawer.
	const handleKeyDown = event => {
		if (event.key !== 'Escape') return;
		event.stopPropagation();
		onCancel();
	};

	return (
		<Flex align="center" gap={4} wrap={false} onKeyDown={handleKeyDown} style={style}>
			<ColorPicker
				size="small"
				value={draft.color}
				onChange={color => setDraft(d => ({ ...d, color: color.toHexString() }))}
				aria-label="Class color"
			/>
			<Input
				size="small"
				value={draft.label}
				onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
				onPressEnter={onSave}
				style={{ flex: 1, minWidth: 60 }}
				placeholder="Class name"
				aria-label="Class label"
				autoFocus
			/>
			{/* Suffix rather than addonAfter: the addon's box costs width this row lacks. */}
			<InputNumber
				size="small"
				value={draft.pricePerM2}
				onChange={pricePerM2 => setDraft(d => ({ ...d, pricePerM2: pricePerM2 ?? 0 }))}
				onPressEnter={onSave}
				min={0}
				step={0.1}
				placeholder="0.00"
				controls={false}
				suffix={
					<Text type="secondary" style={{ fontSize: '0.7rem' }}>
						{currency.symbol || 'unit'}/m²
					</Text>
				}
				style={{ width: 96, flexShrink: 0 }}
				aria-label="Class price per m²"
			/>
			<Tooltip title="Save">
				<Button
					type="text"
					size="small"
					icon={<LuCheck />}
					onClick={onSave}
					aria-label="Save class"
				/>
			</Tooltip>
			<Tooltip title="Cancel">
				<Button
					type="text"
					size="small"
					icon={<LuX />}
					onClick={onCancel}
					aria-label="Cancel editing class"
				/>
			</Tooltip>
		</Flex>
	);
};

export default ClassEditRow;
